import axios from 'axios';

/**
 * Which Graph Curation datasets have recommendations configured for a client.
 *
 * Probes `GET /v1/cids/:cid/curation/config?dataset=…` for merge and split. A dataset is available
 * when the config names a recommendation table — the same rule the page uses to load it, so a
 * dataset button never shows for a config that then loads "No recommendations found." A 404 or a
 * config without a table counts as "not configured"; any other failure (5xx, timeout) leaves the
 * dataset shown, so a passing outage never hides a tab the client does have.
 *
 * Cached per graph service and cid for the session — the Recommendations tab strip and the page
 * both ask, and the answer does not change while the user is looking at it. The service is part of
 * the key because it changes with the selected region/cloud.
 */
const cache = new Map();

async function probe(store, cid, dataset) {
    try {
        const res = await axios.get(`${store.state.graphServiceUrl}/v1/cids/${encodeURIComponent(cid)}/curation/config`, {
            params: { dataset },
            headers: { Authorization: `Bearer ${store.state.accessToken}` },
            /* A 404 is an expected answer here, not an error to toast. */
            errorHandle: false,
        });
        const tableName = res?.data?.recommendation_table_name || null;
        return { available: !!tableName, tableName };
    } catch (e) {
        return { available: e.response?.status !== 404, tableName: null };
    }
}

/**
 * @param {Object} store - Vuex store
 * @param {string} cid
 * @returns {Promise<{merge: boolean, split: boolean, tables: {merge: ?string, split: ?string}}>}
 *   `tables` holds the recommendation table each probe found, or null when it found none
 */
export function getCurationAvailability(store, cid) {
    if (!cid) return Promise.resolve({ merge: true, split: true, tables: { merge: null, split: null } });
    const key = `${store.state.graphServiceUrl}::${cid}`;
    if (!cache.has(key)) {
        const p = Promise.all([probe(store, cid, 'merge'), probe(store, cid, 'split')])
            .then(([merge, split]) => ({
                merge: merge.available,
                split: split.available,
                tables: { merge: merge.tableName, split: split.tableName },
            }));
        cache.set(key, p);
    }
    return cache.get(key);
}
